import { Box, Typography } from "@mui/material";
import { DotLegalLink } from "@dotlegal/dotlegal-ui-components";
import { EntityType } from "@common/Common.types";
import { useTranslation } from "@localization/useTranslation";
import { useUrlProvider } from "../../../useUrlProvider";
import { useEntityGraphColors, useEntityGraphStyles } from "./EntityGraph.styles";
import { EntityGraphNode, EntityGraphOverlay, parseEntityGraphNodeKey } from "./EntityGraph.types";
import { externalLinkUrl } from "./EntityGraph.utils";
import { InspectorItem } from "./EntityGraph.hooks";
import { getLink } from "../../../taskManagement/taskPane/taskRelationsBox/TaskRelationsBox.hooks";

interface EntityGraphInspectorProps {
    rootNode: EntityGraphNode | undefined;
    blastRadius: Array<InspectorItem>;
    dependencies: Array<InspectorItem>;
    associated: Array<InspectorItem>;
    onRootChange: (key: string) => void;
    overlay?: EntityGraphOverlay;
}

function EntityGraphInspector({ rootNode, blastRadius, dependencies, associated, onRootChange, overlay }: EntityGraphInspectorProps) {
    const styles = useEntityGraphStyles();
    const colors = useEntityGraphColors();
    const { translateString } = useTranslation();
    const urlProvider = useUrlProvider();

    if (!rootNode) {
        return <Box sx={styles.inspector} />;
    }

    const rootDecoration = overlay?.decorateNode?.(rootNode);

    const entityOverviewUrl = (node: EntityGraphNode) => getLink(node.typeId as EntityType, parseEntityGraphNodeKey(node.key).id, undefined, urlProvider);

    // The row still re-roots the graph on click; the name is a link out to the entity's own page, so it stops
    // propagation to keep the two actions distinct.
    const itemList = (items: Array<InspectorItem>, emptyText: string) =>
        items.length === 0 ? (
            <Typography sx={styles.depEmpty}>{emptyText}</Typography>
        ) : (
            <Box sx={styles.depList}>
                {items.map((item) => (
                    <Box
                        key={item.node.key}
                        sx={styles.depItem}
                        role="button"
                        tabIndex={0}
                        onClick={() => onRootChange(item.node.key)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                onRootChange(item.node.key);
                            }
                        }}
                    >
                        <Box sx={styles.depItemSwatch} style={{ backgroundColor: item.decoration?.stroke ?? colors.nodeStroke }} />
                        <Box sx={styles.depItemName}>
                            <DotLegalLink to={entityOverviewUrl(item.node)} stopPropagation>
                                {item.node.label}
                            </DotLegalLink>
                        </Box>
                        <Box sx={styles.depItemType}>{item.node.typeLabel}</Box>
                        <Box sx={styles.depItemHop}>
                            {item.hops} {translateString(item.hops === 1 ? "entityGraphHopSingle" : "entityGraphHopPlural")}
                        </Box>
                    </Box>
                ))}
            </Box>
        );

    return (
        <Box sx={styles.inspector}>
            <Typography sx={styles.inspectorHeading}>{translateString("entityGraphRootEntity")}</Typography>
            <Typography sx={styles.inspectorName}>
                <DotLegalLink to={entityOverviewUrl(rootNode)} linkColor="primary">
                    {rootNode.label}
                </DotLegalLink>
            </Typography>
            <Box sx={styles.inspectorTypeChip}>{rootNode.typeLabel}</Box>

            {rootDecoration?.chip && <Box sx={styles.rowBlock}>{rootDecoration.chip}</Box>}

            {overlay?.renderInspectorExtras?.(rootNode)}

            <Box sx={styles.metricRow}>
                <Box sx={styles.metric}>
                    <Box component="span" sx={styles.metricLabel}>
                        {translateString("entityGraphSupports")}
                    </Box>
                    <Box component="span" sx={styles.metricValue}>
                        {blastRadius.length}
                    </Box>
                </Box>
                <Box sx={styles.metric}>
                    <Box component="span" sx={styles.metricLabel}>
                        {translateString("entityGraphDependsOn")}
                    </Box>
                    <Box component="span" sx={styles.metricValue}>
                        {dependencies.length}
                    </Box>
                </Box>
            </Box>

            {associated.length > 0 && (
                <Box sx={styles.rowBlock}>
                    <Typography sx={styles.rowBlockLabel}>{translateString("entityGraphAssociatedSystems", { count: associated.length })}</Typography>
                    {itemList(associated, "")}
                </Box>
            )}

            {(overlay || rootDecoration) && (
                <>
                    <Box sx={styles.rowBlock}>
                        <Typography sx={styles.rowBlockLabel}>{translateString("entityGraphBlastRadius", { count: blastRadius.length })}</Typography>
                        {itemList(blastRadius, translateString("entityGraphNothingDepends"))}
                    </Box>

                    <Box sx={styles.rowBlock}>
                        <Typography sx={styles.rowBlockLabel}>{translateString("entityGraphOwnDependencies", { count: dependencies.length })}</Typography>
                        {itemList(dependencies, translateString("entityGraphNoDependencies"))}
                    </Box>
                </>
            )}

            {rootNode.leafChips.length > 0 && (
                <Box sx={styles.rowBlock}>
                    <Typography sx={styles.rowBlockLabel}>{translateString("entityGraphFreeTextSubProcessors")}</Typography>
                    <Box sx={styles.depList}>
                        {rootNode.leafChips.map((chip) => (
                            <Box
                                key={chip}
                                component="a"
                                href={externalLinkUrl(chip)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={[styles.depItem, styles.depItemLink]}
                            >
                                <Box sx={styles.depItemName}>{chip}</Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export default EntityGraphInspector;
